import CallReopenAction from '@/components/CallReopenAction';
import {isCallClosed} from '@/utils/callPresentation';
import InfiniteScroll from '@/components/InfiniteScroll';
import useInfiniteRecords from '@/hooks/useInfiniteRecords';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    ChevronRight,
    AlertTriangle, CalendarDays, Eye, FilterX,
    ListFilter, Plus, RefreshCw, Search, SlidersHorizontal, TicketCheck, UserRoundSearch,
} from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import api from '@/api/axios';
import { useAuth } from '@/auth/AuthContext';
import { CallSummary } from '@/types/call';
import { Category, Local, Unit, UserEntity } from '@/types/types';
import {
    CALL_PRIORITY_LABELS, CALL_PRIORITY_STYLES, CALL_STATUS_LABELS, CALL_STATUS_STYLES,
    formatCallDate, formatCallProtocol,
} from '@/utils/callPresentation';

interface CallFilters {
    search: string;
    status: string;
    priority: string;
    categoryId: string;
    responsibleTechnicianId: string;
    unitId: string;
    localId: string;
    startDate: string;
    endDate: string;
    assignment: string;
}

const emptyFilters: CallFilters = {
    search: '', status: '', priority: '', categoryId: '',
    responsibleTechnicianId: '', unitId: '', localId: '', startDate: '', endDate: '',
    assignment: '',
};

const fieldClass = 'form-field min-h-11 py-2.5 text-xs';
const labelClass = 'mb-1.5 block text-[9px] font-black uppercase tracking-[0.16em] text-slate-500';

const badge = (label: string, className: string) => (
    <span className={`inline-flex rounded-full border px-2.5 py-1 text-[9px] font-black uppercase tracking-wider ${className}`}>
        {label}
    </span>
);

export default function CallListPage() {
    const navigate = useNavigate();const location=useLocation();
    const { activeSecretary, user } = useAuth();
    const canCreateCalls = ['ADMIN', 'TECHNICAL_ADMIN', 'TECHNICAL'].includes(user?.role || '');
    const [filters, setFilters] = useState<CallFilters>(emptyFilters);
    const [error, setError] = useState('');
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

    const [categories, setCategories] = useState<Category[]>([]);
    const [technicians, setTechnicians] = useState<UserEntity[]>([]);
    const [units, setUnits] = useState<Unit[]>([]);
    const [locations, setLocations] = useState<Local[]>([]);

    const activeFilterCount = useMemo(
        () => Object.values(filters).filter(value => value !== '').length,
        [filters],
    );

    const updateFilter = <K extends keyof CallFilters>(key: K, value: CallFilters[K]) => {
        setFilters(current => {
            const next = { ...current, [key]: value };
            if (key === 'unitId') next.localId = '';
            return next;
        });
    };

    useEffect(() => {
        if (!activeSecretary?.id) return;
        let mounted = true;
        Promise.all([
            api.get('/category/all', { params: { size: 100 } }),
            api.get('/users/technicians'),
            api.get(`/unit/secretary/${activeSecretary.id}`),
        ]).then(([categoryResponse, technicianResponse, unitResponse]) => {
            if (!mounted) return;
            setCategories(categoryResponse.data.content ?? categoryResponse.data ?? []);
            setTechnicians(technicianResponse.data ?? []);
            setUnits(unitResponse.data.content ?? unitResponse.data ?? []);
        }).catch(() => mounted && setError('Não foi possível carregar as opções dos filtros.'));
        return () => { mounted = false; };
    }, [activeSecretary?.id]);

    useEffect(() => {
        if (!filters.unitId) {
            setLocations([]);
            return;
        }
        let mounted = true;
        api.get(`/local/unit/${filters.unitId}`)
            .then(response => mounted && setLocations(response.data.content ?? response.data ?? []))
            .catch(() => mounted && setLocations([]));
        return () => { mounted = false; };
    }, [filters.unitId]);

    const params: Record<string, unknown> = {};
    Object.entries(filters).forEach(([key, value]) => { if (value) { if (key === 'assignment') params.unassigned = value === 'UNASSIGNED'; else params[key] = value; } });
    const query = useInfiniteRecords<CallSummary>('/calls/filter', params, activeSecretary?.id);
    const { items: calls, loading, total: totalElements } = query;
    const clearFilters = () => {
        setFilters(emptyFilters);
        setLocations([]);
    };

    return (
        <div className="page-content">
            <motion.section
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                className="ui-card flex flex-col gap-5 overflow-visible md:flex-row md:items-center md:justify-between"
            >
                <div>
                    <div className="mb-2 flex items-center gap-2 text-blue-400">
                        <TicketCheck size={18} />
                        <span className="text-[10px] font-black uppercase tracking-[0.2em]">Central operacional</span>
                    </div>
                    <h2 className="text-xl font-black text-white">{totalElements} chamado{totalElements === 1 ? '' : 's'} encontrado{totalElements === 1 ? '' : 's'}</h2>
                    <p className="mt-1 text-xs text-slate-500">Acompanhe protocolos, responsáveis, prazos e classificações da secretaria ativa.</p>
                </div>
                {canCreateCalls && (
                    <button type="button" onClick={() => navigate('/tickets/new')} className="ui-button-primary shrink-0">
                        <Plus size={17} /> Novo chamado
                    </button>
                )}
            </motion.section>

            <motion.section
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
                className="ui-card overflow-visible"
            >
                <div className="mb-5 flex flex-wrap items-center justify-between gap-3 border-b border-white/5 pb-4">
                    <div className="flex items-center gap-2">
                        <ListFilter size={16} className="text-blue-400" />
                        <h2 className="text-xs font-black uppercase tracking-wider text-white">Filtros da consulta</h2>
                        {activeFilterCount > 0 && <span className="rounded-full bg-blue-500/15 px-2 py-0.5 text-[9px] font-black text-blue-300">{activeFilterCount}</span>}
                    </div>
                    <div className="flex gap-2">
                        <button type="button" onClick={() => setShowAdvancedFilters(value => !value)} className="ui-button-secondary px-3 py-2">
                            <SlidersHorizontal size={14} /> {showAdvancedFilters ? 'Menos filtros' : 'Mais filtros'}
                        </button>
                        {activeFilterCount > 0 && (
                            <button type="button" onClick={clearFilters} className="ui-button-secondary px-3 py-2 text-red-300">
                                <FilterX size={14} /> Limpar
                            </button>
                        )}
                    </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                    <div className="relative md:col-span-2">
                        <label className={labelClass}>Busca geral</label>
                        <Search size={15} className="absolute bottom-3.5 left-4 text-slate-600" />
                        <input
                            value={filters.search}
                            onChange={event => updateFilter('search', event.target.value)}
                            className={`${fieldClass} pl-11`}
                            placeholder="Protocolo completo, relato, servidor, patrimônio, local..."
                        />
                    </div>
                    <div>
                        <label className={labelClass}>Status</label>
                        <select className={fieldClass} value={filters.status} onChange={event => updateFilter('status', event.target.value)}>
                            <option value="">Todos os status</option>
                            {Object.entries(CALL_STATUS_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className={labelClass}>Prioridade</label>
                        <select className={fieldClass} value={filters.priority} onChange={event => updateFilter('priority', event.target.value)}>
                            <option value="">Todas as prioridades</option>
                            {Object.entries(CALL_PRIORITY_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                        </select>
                    </div>
                </div>

                <AnimatePresence initial={false}>
                    {showAdvancedFilters && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                            className="overflow-hidden"
                        >
                            <div className="mt-4 grid gap-4 border-t border-white/5 pt-4 md:grid-cols-2 xl:grid-cols-4">
                                <div><label className={labelClass}>Categoria</label><select className={fieldClass} value={filters.categoryId} onChange={event => updateFilter('categoryId', event.target.value)}><option value="">Todas</option>{categories.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></div>
                                <div><label className={labelClass}>Unidade</label><select className={fieldClass} value={filters.unitId} onChange={event => updateFilter('unitId', event.target.value)}><option value="">Todas</option>{units.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></div>
                                <div><label className={labelClass}>Local</label><select className={fieldClass} value={filters.localId} disabled={!filters.unitId} onChange={event => updateFilter('localId', event.target.value)}><option value="">Todos</option>{locations.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></div>
                                <div><label className={labelClass}>Técnico responsável</label><select className={fieldClass} value={filters.responsibleTechnicianId} onChange={event => updateFilter('responsibleTechnicianId', event.target.value)}><option value="">Todos</option>{technicians.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></div>
                                <div><label className={labelClass}>Atribuição</label><select className={fieldClass} value={filters.assignment} onChange={event => updateFilter('assignment', event.target.value)}><option value="">Todos</option><option value="UNASSIGNED">Sem técnico</option><option value="ASSIGNED">Com técnico</option></select></div>
                                <div><label className={labelClass}>Abertos a partir de</label><input type="date" className={fieldClass} value={filters.startDate} onChange={event => updateFilter('startDate', event.target.value)} /></div>
                                <div><label className={labelClass}>Abertos até</label><input type="date" className={fieldClass} value={filters.endDate} onChange={event => updateFilter('endDate', event.target.value)} /></div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.section>

            {location.state?.notice && <p role="status" className="feedback-success">{location.state.notice}</p>}
            {error && <div className="feedback-error flex items-center gap-2"><AlertTriangle size={17} /> {error}</div>}

            <section className="ui-card p-0 md:p-0">
                {loading && !calls.length ? (
                    <div className="flex min-h-72 items-center justify-center gap-3 text-sm font-bold text-blue-300"><RefreshCw className="animate-spin" size={20} /> Consultando chamados...</div>
                ) : calls.length === 0 ? (
                    <div className="flex min-h-72 flex-col items-center justify-center p-8 text-center">
                        <UserRoundSearch size={38} className="mb-4 text-slate-700" />
                        <h3 className="font-black text-white">Nenhum chamado encontrado</h3>
                        <p className="mt-2 max-w-md text-xs text-slate-500">Altere os filtros para ampliar a consulta{canCreateCalls ? ' ou registre um novo chamado para a secretaria ativa' : ''}.</p>
                    </div>
                ) : (
                    <>
                        <div className="hidden overflow-x-auto lg:block">
                            <table className="w-full min-w-[1050px] text-left">
                                <thead className="border-b border-white/5 bg-white/[0.02] text-[9px] font-black uppercase tracking-wider text-slate-500">
                                    <tr><th className="px-5 py-4">Protocolo / abertura</th><th className="px-4 py-4">Solicitante e relato</th><th className="px-4 py-4">Classificação</th><th className="px-4 py-4">Atendimento</th><th className="px-4 py-4">Situação</th><th className="px-5 py-4 text-right">Ação</th></tr>
                                </thead>
                                <tbody className="divide-y divide-white/5">
                                    {calls.map((call, index) => (
                                        <motion.tr key={call.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(index * 0.025, 0.2) }} onClick={() => navigate(`/tickets/${call.id}`)} className="cursor-pointer transition-colors hover:bg-blue-500/[0.04]">
                                            <td className="px-5 py-4"><strong className="block text-xs text-blue-300">{formatCallProtocol(call.id)}</strong><span className="mt-1 flex items-center gap-1 text-[10px] text-slate-600"><CalendarDays size={11} /> {formatCallDate(call.openingDate)}</span></td>
                                            <td className="max-w-[310px] px-4 py-4"><strong className="block truncate text-xs text-slate-200">{call.applicantName || 'Solicitante não informado'}</strong><p className="mt-1 line-clamp-2 text-[10px] leading-4 text-slate-500">{call.description}</p></td>
                                            <td className="px-4 py-4"><span className="block text-xs text-slate-300">{call.categoryName || 'Sem categoria'}</span><span className="mt-1 block max-w-[190px] truncate text-[10px] text-slate-600">{call.subcategoryName || call.callTypeName || 'Não classificado'}</span></td>
                                            <td className="px-4 py-4"><span className="block max-w-[180px] truncate text-xs text-slate-300">{call.responsibleTechnicianName || 'Sem técnico atribuído'}</span><span className="mt-1 block max-w-[180px] truncate text-[10px] text-slate-600">{call.unitName}{call.localName ? ` / ${call.localName}` : ''}</span></td>
                                            <td className="space-y-1.5 px-4 py-4">{badge(CALL_STATUS_LABELS[call.status], CALL_STATUS_STYLES[call.status])}<br />{badge(CALL_PRIORITY_LABELS[call.priority], CALL_PRIORITY_STYLES[call.priority])}</td>
                                            <td className="px-5 py-4 text-right"><CallReopenAction id={call.id} status={call.status} onReopened={query.reload}/>{canCreateCalls && !isCallClosed(call.status) && <button type="button" onClick={event=>{event.stopPropagation();navigate(`/tickets/${call.id}/edit`);}} className="ui-button-secondary mr-2">Editar</button>}<button type="button" onClick={event => { event.stopPropagation(); navigate(`/tickets/${call.id}`); }} className="rounded-xl border border-white/5 bg-white/5 p-2.5 text-slate-400 transition hover:border-blue-500/30 hover:text-blue-300" aria-label={`Visualizar ${formatCallProtocol(call.id)}`}><Eye size={16} /></button></td>
                                        </motion.tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="divide-y divide-white/5 lg:hidden">
                            {calls.map(call => (
                                <article key={call.id} onClick={() => navigate(`/tickets/${call.id}`)} className="block w-full p-4 text-left transition hover:bg-white/[0.03]">
                                    <div className="flex items-start justify-between gap-3"><div><strong className="text-xs text-blue-300">{formatCallProtocol(call.id)}</strong><p className="mt-1 text-[10px] text-slate-600">{formatCallDate(call.openingDate)}</p></div><ChevronRight size={17} className="text-slate-600" /></div>
                                    <h3 className="mt-3 text-xs font-bold text-white">{call.applicantName || 'Solicitante não informado'}</h3>
                                    <p className="mt-1 line-clamp-2 text-[11px] leading-5 text-slate-500">{call.description}</p>
                                    <div className="mt-3 flex flex-wrap gap-2">{badge(CALL_STATUS_LABELS[call.status], CALL_STATUS_STYLES[call.status])}{badge(CALL_PRIORITY_LABELS[call.priority], CALL_PRIORITY_STYLES[call.priority])}</div>
                                    <p className="mt-3 truncate text-[10px] text-slate-600">{call.categoryName || 'Sem categoria'} · {call.responsibleTechnicianName || 'Sem técnico'}</p><div className="mt-3 flex gap-2"><CallReopenAction id={call.id} status={call.status} onReopened={query.reload}/><button className="ui-button-secondary" onClick={e=>{e.stopPropagation();navigate(`/tickets/${call.id}`);}}>Visualizar</button>{canCreateCalls && !isCallClosed(call.status) && <button className="ui-button-secondary" onClick={e=>{e.stopPropagation();navigate(`/tickets/${call.id}/edit`);}}>Editar</button>}</div>
                                </article>
                            ))}
                        </div>
                    </>
                )}

                {!loading && calls.length > 0 && (
                    <footer className="flex flex-col gap-3 border-t border-white/5 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
                        {query.error && <p role="alert" className="feedback-error">{query.error}</p>}<InfiniteScroll hasMore={query.hasMore} loading={loading} onLoadMore={query.loadMore} />

                    </footer>
                )}
            </section>
        </div>
    );
}
